import DownArrowIcon from "@icons/DownArrow";
import { ReactComponent as CloseCircleIcon } from "@icons/close-circle.svg";
import CloseIcon from "@icons/close.svg";

import Colors from "@constants/Colors";
import LocationContext from "@context/location/locationContext";
import Header from "@shared-layouts/Header";
import AutoCompleteField from "@shared-ui-components/Fields/AutoCompleteField";
import SelectDropdownField from "@shared-ui-components/Fields/SelectDropdownField";
import { useContext, useEffect, useState } from "react";
import { getApiResponse } from "@src/utils/service";
import authContext from "@src/context/Auth/authContext";
import ShowErrorToastContext from "@context/showErrorToast/showErrorToastContext";
import Loader from "../Loader";
import { StyledBox, StyledButton, LocationSelectorWrapper } from "./styles";
import {
  LocationDetails,
  LocationIds,
  LocationSelectorProps,
  PropertyType,
  RevenueCenterType,
  StoreType,
} from "./types";

const LocationSelectorDialog = ({
  currentLocationIds,
  currentLocation,
  open,
  onClose,
}: LocationSelectorProps) => {
  const [propertyOptions, setPropertyOptions] = useState<PropertyType[]>([]);
  const [storeOptions, setStoreOptions] = useState<StoreType[]>([]);
  const [revenueCenterOptions, setRevenueCenterOptions] = useState<
    RevenueCenterType[]
  >([]);
  const [locationIds, setLocationIds] =
    useState<LocationIds>(currentLocationIds);
  const [location, setLocation] = useState<LocationDetails>(currentLocation);
  const [{ locationState, locationSetState }] = useContext(LocationContext);
  const [{ state }] = useContext(authContext);

  const [{ showErrorToastSetState }] = useContext(ShowErrorToastContext);
  const isMLSE = location?.property === "MLSE";
  const [loading, setLoading] = useState(true);
  const [nodata, setNoData] = useState<{ key: string | null }>({ key: null });

  const locationPath = () => {
    const { property, store } = location;
    if (property.length > 0 && store.length <= 0) {
      return property;
    }
    if (property.length > 0 && store.length > 0) {
      const propertyShortname = propertyOptions.find(
        (el) => el.property_name === property
      )?.short_name;
      return `${propertyShortname} ${store}`;
    }
    return property.length > 0 ? property : "MLSE";
  };

  const setLocallocationDetails = () => {
    localStorage.setItem("locationIds", JSON.stringify(locationIds));
    localStorage.setItem("locationDetails", JSON.stringify(location));

    const updatedLocationState = {
      ...locationState,
      property: locationIds.property,
      store: locationIds.store_id,
      revenueCenter: locationIds.rvc_id,
    };
    locationSetState(updatedLocationState);
    onClose(locationPath());
  };

  const handlePropertyChange = async (value: string) => {
    const id =
      propertyOptions?.find((el) => el?.property_name === value)?.id || "";
    if (value !== "MLSE" && value.length) {
      const storeResData = await getApiResponse(`location?property=${id}`);
      setLoading(false);
      if (storeResData.data !== null) {
        setStoreOptions(storeResData.data);
      } else {
        setNoData({ key: "store" });
        showErrorToastSetState({
          errorMessage: storeResData?.error || "",
          errorTitle: "Unable to Fetch Store Content",
          open: true,
        });
      }
    }
    setLocationIds({ store_id: "", rvc_id: "", property: id });
    const propertyShortname = propertyOptions.find(
      (el) => el.property_name === value
    )?.short_name;
    setLocation({
      store: "",
      revenueCenter: "",
      property: value,
      propertyShortname: propertyShortname || "",
    });
  };

  const handleStoreChange = async (value: string) => {
    const id = storeOptions?.find((el) => el?.store_name === value)?.id || "";
    const rvcResData = await getApiResponse(
      `location?property=${locationIds.property}&store=${id}`
    );
    setLoading(false);

    if (rvcResData.data !== null) {
      setRevenueCenterOptions(rvcResData.data);
    } else {
      setNoData({ key: "rvc" });
      showErrorToastSetState({
        errorMessage: rvcResData?.error || "",
        errorTitle: "Unable to Fetch Rvc Content",
        open: true,
      });
    }

    setLocationIds((prev) => {
      return { ...prev, store_id: id, rvc_id: "" };
    });
    setLocation((prev) => {
      return { ...prev, store: value, revenueCenter: "" };
    });
  };

  const handleRevenueCenterChange = (value: string) => {
    const id =
      revenueCenterOptions?.find((el) => el?.rvc_name === value)?.id || "";

    setLocationIds((prev) => {
      return { ...prev, rvc_id: id };
    });
    setLocation((prev) => {
      return { ...prev, revenueCenter: value };
    });
  };

  useEffect(() => {
    setLoading(true);
    (async () => {
      const propertyResData = await getApiResponse("location");
      if (propertyResData.data === null) setNoData({ key: "property" });
      if (propertyResData.data !== null) {
        setPropertyOptions(propertyResData.data);
      } else {
        showErrorToastSetState({
          errorMessage: propertyResData?.error || "",
          errorTitle: "Unable to Fetch Property Content",
          open: true,
        });
      }
      if (location?.property?.length > 0 && !isMLSE) {
        const storeResData = await getApiResponse(
          `location?property=${locationIds.property}`
        );
        if (storeResData.data !== null) {
          setStoreOptions(storeResData.data);
        } else {
          setNoData({ key: "store" });
          showErrorToastSetState({
            errorMessage: storeResData?.error || "",
            errorTitle: "Unable to Fetch Store Content",
            open: true,
          });
        }
      }
      if (location?.store?.length > 0 && !isMLSE) {
        const rvcResData = await getApiResponse(
          `location?property=${locationIds.property}&store=${locationIds.store_id}`
        );
        if (rvcResData.data !== null) {
          setRevenueCenterOptions(rvcResData.data);
        } else {
          setNoData({ key: "rvc" });
          showErrorToastSetState({
            errorMessage: rvcResData?.error || "",
            errorTitle: "Unable to Fetch RVC Content",
            open: true,
          });
        }
      }
      setLoading(false);
    })();
  }, []);

  const handleChange = async (key: string, value: string) => {
    if (key === "property") {
      await handlePropertyChange(value);
    }
    if (key === "store") {
      handleStoreChange(value);
    }
    if (key === "revenueCenter") {
      handleRevenueCenterChange(value);
    }
  };

  const handleAutocompleteChange = (value: string) =>
    handleRevenueCenterChange(value);

  const handleClose = () => onClose(locationPath());

  const propertyDisable =
    (Number(state?.level) !== 1 && Number(state?.level) !== 4) || false;
  const storeDisable =
    Number(state?.level) === 3 ||
    isMLSE ||
    location.property === "" ||
    nodata?.key === "property";
  const rvcDisable = isMLSE || location.store === "" || nodata?.key === "store";

  return (
    <LocationSelectorWrapper open={open} onClose={handleClose} fullScreen>
      <Header
        heading="Location Selection"
        icon={CloseIcon}
        handleClick={handleClose}
        showIcon
      />
      {loading ? (
        <Loader />
      ) : (
        <StyledBox>
          <SelectDropdownField
            labelKey="property"
            value={location?.property}
            label="Property"
            disabled={propertyDisable}
            options={propertyOptions?.map((el) => el?.property_name)}
            handleChange={handleChange}
            nodata={nodata?.key === "property"}
          />
          <SelectDropdownField
            labelKey="store"
            value={location.store || ""}
            label="Store"
            disabled={storeDisable}
            options={storeOptions?.map((el) => el?.store_name)}
            handleChange={handleChange}
            nodata={nodata?.key === "store"}
          />
          <AutoCompleteField
            disabled={rvcDisable}
            value={location?.revenueCenter || ""}
            label="Revenue Center"
            options={revenueCenterOptions}
            handleChange={handleAutocompleteChange}
            PopupIcon={DownArrowIcon({ strokeColor: Colors.neutral_gray_500 })}
            ClearIcon={CloseCircleIcon}
            nodata={nodata?.key === "rvc"}
          />
          <StyledButton
            disabled={nodata?.key !== null || location?.property?.length <= 0}
            onClick={setLocallocationDetails}
          >
            Go To Dashboard
          </StyledButton>
        </StyledBox>
      )}
    </LocationSelectorWrapper>
  );
};
export default LocationSelectorDialog;
